import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const solutions = [
  { 
    icon: '👥', 
    title: 'Small Practice Medical Billing', 
    description: 'Tailored billing solutions for small practices with limited staff. Reduce overhead costs by 30-50% while improving claim accuracy and cash flow.',
    link: '/medical-billing-outsourcing-small-practices'
  },
  { 
    icon: '👨‍⚕️', 
    title: 'Independent Physician RCM', 
    description: 'Comprehensive revenue cycle management for solo practitioners and independent physicians. Focus on patients while we handle billing.',
    link: '/revenue-cycle-management-independent-physicians'
  },
  { 
    icon: '🏢', 
    title: 'Multispecialty Practice Solutions', 
    description: 'Complex billing management for multispecialty clinics with diverse coding needs and multiple payer contracts.',
    link: '/revenue-cycle-management-multispecialty-practices'
  },
  { 
    icon: '🛡️', 
    title: 'Denial Management Services', 
    description: 'Expert denial prevention and resolution to reduce claim denials by 35-45%. Recover lost revenue and prevent future denials.',
    link: '/denial-management-services-medical-clinics'
  },
  { 
    icon: '📉', 
    title: 'Reduce Claim Denials', 
    description: 'Proven strategies to reduce claim denials in healthcare billing. AI-powered validation catches errors before submission.',
    link: '/reduce-claim-denials-healthcare-billing'
  },
  { 
    icon: '⭐', 
    title: 'Outsourcing Benefits', 
    description: 'Experience all benefits of medical billing outsourcing: lower costs, faster payments, expert support, and better compliance.',
    link: '/benefits-medical-billing-outsourcing-clinics'
  },
  { 
    icon: '⚡', 
    title: 'Automated Claims Processing', 
    description: 'Reduce manual entry errors with AI that validates and submits claims within 24 hours for faster reimbursements.',
    link: '/medical-billing-outsourcing'
  },
  { 
    icon: '📊', 
    title: 'Real-Time Revenue Analytics', 
    description: 'Track revenue KPIs, payment trends, and collections with comprehensive dashboards designed for small practices.',
    link: '/hospital-revenue-cycle-management'
  },
];

const SolutionSection = () => {
  return (
    <section
      style={{
        padding: '80px 5%',
        backgroundColor: '#ffffff',
        fontFamily: "'Poppins', sans-serif",
      }}
      itemScope
      itemType="https://schema.org/ItemList"
      role="region"
      aria-label="Medical billing outsourcing solutions"
    >
      {/* ✅ SEO-OPTIMIZED H2 Header with NEW KEYWORDS */}
      <h2
        style={{
          fontSize: 'clamp(1.8rem, 4vw, 2.8rem)',
          fontWeight: '700',
          textAlign: 'center',
          marginBottom: '20px',
          color: '#1A1A1A',
          lineHeight: '1.3',
        }}
        itemProp="name"
      >
        Medical Billing Outsourcing Solutions for Small Practices & Independent Physicians
      </h2>

      {/* Supporting paragraph with keywords */}
      <p style={{
        fontSize: '1.1rem',
        textAlign: 'center',
        maxWidth: '900px',
        margin: '0 auto 50px',
        lineHeight: '1.7',
        color: '#444',
      }}
      itemProp="description"
      >
        Our comprehensive solutions combine expert{' '}
        <Link 
          to="/revenue-cycle-management-independent-physicians" 
          style={{ color: '#0077b6', textDecoration: 'underline', fontWeight: '600' }}
          aria-label="Learn about revenue cycle management for independent physicians"
        >
          revenue cycle management
        </Link>{' '}
        with specialized{' '}
        <Link 
          to="/denial-management-services-medical-clinics" 
          style={{ color: '#0077b6', textDecoration: 'underline', fontWeight: '600' }}
          aria-label="Explore denial management services"
        >
          denial management services
        </Link>. Discover how to{' '}
        <Link 
          to="/reduce-claim-denials-healthcare-billing" 
          style={{ color: '#0077b6', textDecoration: 'underline', fontWeight: '600' }}
          aria-label="Learn how to reduce claim denials"
        >
          reduce claim denials in healthcare billing
        </Link>:
      </p>

      {/* Solutions Grid */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: '25px',
          maxWidth: '1400px',
          margin: '0 auto',
        }}
        itemProp="itemListElement"
      >
        {solutions.map((sol, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 50, rotateY: -10 }}
            whileInView={{ opacity: 1, y: 0, rotateY: 0 }}
            transition={{ duration: 0.5, delay: index * 0.08 }}
            viewport={{ once: true }}
            style={{
              background: '#ffffff',
              borderRadius: '14px',
              padding: '30px 25px',
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.06)',
              flex: '1 1 320px',
              maxWidth: '350px',
              minWidth: '280px',
              textAlign: 'center',
              transition: 'transform 0.3s ease, box-shadow 0.3s ease',
              perspective: '800px',
              transformStyle: 'preserve-3d',
              border: '1px solid #f0f0f0',
              position: 'relative',
            }}
            whileHover={{
              scale: 1.05,
              rotateX: 2,
              rotateY: -2,
              boxShadow: '0 20px 35px rgba(0, 0, 0, 0.12)',
            }}
            itemProp="itemListElement"
            itemScope
            itemType="https://schema.org/ListItem"
          >
            <meta itemProp="position" content={index + 1} />
            
            <div 
              style={{ fontSize: '3rem', marginBottom: '18px' }}
              aria-hidden="true"
            >
              {sol.icon}
            </div>
            
            <h3 style={{ 
              fontSize: '1.2rem', 
              fontWeight: '700', 
              marginBottom: '12px',
              color: '#0077b6'
            }}
            itemProp="name"
            >
              {sol.title}
            </h3>
            
            <p style={{ 
              fontSize: '0.98rem', 
              color: '#555', 
              lineHeight: '1.7',
              marginBottom: '20px'
            }}
            itemProp="description"
            >
              {sol.description}
            </p>

            {/* CTA Link */}
            <Link 
              to={sol.link}
              style={{
                display: 'inline-block',
                padding: '10px 20px',
                backgroundColor: '#e8f5e9',
                color: '#2e7d32',
                borderRadius: '8px',
                textDecoration: 'none',
                fontSize: '0.9rem',
                fontWeight: '600',
                transition: 'all 0.3s ease',
                border: '1px solid #4caf50',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#2e7d32';
                e.currentTarget.style.color = '#ffffff';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#e8f5e9';
                e.currentTarget.style.color = '#2e7d32';
              }}
              aria-label={`Learn more about ${sol.title}`}
            >
              Learn More →
            </Link>
          </motion.div>
        ))}
      </div>

      {/* Bottom CTA with keywords */}
      <div style={{
        textAlign: 'center',
        marginTop: '60px',
        padding: '30px',
        backgroundColor: '#f8f9fa',
        borderRadius: '12px',
        maxWidth: '900px',
        margin: '60px auto 0',
      }}>
        <h3 style={{
          fontSize: '1.5rem',
          fontWeight: '600',
          marginBottom: '15px',
          color: '#0077b6',
        }}>
          Experience the Benefits of Medical Billing Outsourcing
        </h3>
        <p style={{
          fontSize: '1.05rem',
          lineHeight: '1.7',
          color: '#555',
          marginBottom: '20px',
        }}>
          Join hundreds of small practices and independent physicians using our{' '}
          <Link 
            to="/medical-billing-outsourcing-small-practices" 
            style={{ color: '#0077b6', textDecoration: 'underline', fontWeight: '600' }}
          >
            medical billing outsourcing services
          </Link>{' '}
          for multispecialty practices to achieve 30-50% cost reduction.
        </p>

        {/* Feature Highlights with SERVICE LINKS */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '20px',
          marginTop: '30px',
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', marginBottom: '8px' }}>👥</div>
            <div style={{ fontSize: '0.95rem', color: '#666' }}>
              <strong>Small Practices</strong>
              <br />
              <Link 
                to="/medical-billing-outsourcing-small-practices" 
                style={{ color: '#0077b6', fontSize: '0.85rem' }}
              >
                View Solutions
              </Link>
            </div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', marginBottom: '8px' }}>👨‍⚕️</div>
            <div style={{ fontSize: '0.95rem', color: '#666' }}>
              <strong>Independent Physicians</strong>
              <br />
              <Link 
                to="/revenue-cycle-management-independent-physicians" 
                style={{ color: '#0077b6', fontSize: '0.85rem' }}
              >
                Explore RCM
              </Link>
            </div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', marginBottom: '8px' }}>🏢</div>
            <div style={{ fontSize: '0.95rem', color: '#666' }}>
              <strong>Multispecialty Clinics</strong>
              <br />
              <Link 
                to="/revenue-cycle-management-multispecialty-practices" 
                style={{ color: '#0077b6', fontSize: '0.85rem' }}
              >
                See Services
              </Link>
            </div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', marginBottom: '8px' }}>🛡️</div>
            <div style={{ fontSize: '0.95rem', color: '#666' }}>
              <strong>Denial Management</strong>
              <br />
              <Link 
                to="/denial-management-services-medical-clinics" 
                style={{ color: '#0077b6', fontSize: '0.85rem' }}
              >
                Reduce Denials
              </Link>
            </div>
          </div>
        </div>

        {/* Main CTA Button */}
        <Link 
          to="/free-trial"
          style={{
            display: 'inline-block',
            marginTop: '30px',
            padding: '15px 35px',
            backgroundColor: '#F26522',
            color: '#ffffff',
            borderRadius: '30px',
            fontSize: '1.05rem',
            fontWeight: '700',
            textDecoration: 'none',
            boxShadow: '0 6px 20px rgba(242, 101, 34, 0.4)',
            transition: 'all 0.3s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#d3541c';
            e.currentTarget.style.transform = 'translateY(-2px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#F26522';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          Start Free Trial - For Small Practices
        </Link>
      </div>

      {/* Mobile Specific Styles */}
      <style>
        {`
          @media (max-width: 768px) {
            section {
              padding: 60px 4%;
            }

            h2 {
              margin-bottom: 15px !important;
            }

            div[style*='flexWrap'] > div {
              flex: 1 1 100% !important;
              max-width: 100% !important;
            }
          }

          @media (max-width: 480px) {
            div[style*='flexWrap'] > div {
              padding: 25px 20px !important;
            }

            div[style*='flexWrap'] > div > h3 {
              font-size: 1.1rem !important;
            }

            div[style*='flexWrap'] > div > p {
              font-size: 0.95rem !important;
            }
          }
        `}
      </style>
    </section>
  );
};

export default SolutionSection;